// apps/web/src/features/bookings/components/progressive-flow/VenueDisplayCard.tsx
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface VenueDisplayCardProps {
  venue: {
    id: string;
    name: string;
    coverImage?: string;
    address: string;
    cityName?: string;
    basePrice: number;
    currency?: string;
  };
}

const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${theme.palette.success.main}`,
  position: 'relative',
}));

const VenueImage = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  objectFit: 'cover',
  flexShrink: 0,
}));

const VenuePlaceholder = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.selected,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const VenueInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const VenueName = styled(Typography)({
  fontSize: '1rem',
  fontWeight: 600,
});

const LocationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: theme.palette.text.secondary,
}));

const LocationText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const SelectedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: theme.spacing(0.5, 1),
  backgroundColor: theme.palette.success.main,
  color: theme.palette.success.contrastText,
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 600,
}));

export function VenueDisplayCard({ venue }: VenueDisplayCardProps) {
  const formatPrice = (price: number, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Selected Venue
      </Typography>
      <CardContainer>
        <SelectedBadge>
          <CheckCircleIcon sx={{ fontSize: 14 }} />
          Selected
        </SelectedBadge>

        {venue.coverImage ? (
          <VenueImage src={venue.coverImage} alt={venue.name} />
        ) : (
          <VenuePlaceholder>
            <LocationOnIcon />
          </VenuePlaceholder>
        )}

        <VenueInfo>
          <VenueName>{venue.name}</VenueName>
          <LocationRow>
            <LocationOnIcon sx={{ fontSize: 16 }} />
            <LocationText>
              {venue.address}
              {venue.cityName && `, ${venue.cityName}`}
            </LocationText>
          </LocationRow>
          <PriceText>{formatPrice(venue.basePrice, venue.currency)} / hour</PriceText>
        </VenueInfo>
      </CardContainer>
    </Box>
  );
}
